/**
 * Performance Optimizer
 * PRO VERSION: Robust handling for Scroll, Resize, and Intersection
 */

(function () {
  'use strict';

  const CONFIG = {
    rootMargin: '200px', // Preload content 200px before viewport
    threshold: 0
  };

  // --- Utilities ---

  // RequestIdleCallback Polyfill
  const requestIdle = window.requestIdleCallback ||
    function (cb) {
      var start = Date.now();
      return setTimeout(function () {
        cb({
          didTimeout: false,
          timeRemaining: function () {
            return Math.max(0, 50 - (Date.now() - start));
          }
        });
      }, 1);
    };

  // Debounce
  function debounce(func, wait, immediate) {
    let timeout;
    return function () {
      const context = this, args = arguments;
      const later = function () {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  }

  // Throttle
  function throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  // --- Core Optimizer ---

  class PerformanceOptimizer {
    constructor() {
      this.observer = null;
      this.init();
    }

    init() {
      if ('IntersectionObserver' in window) {
        this.setupObserver();
      } else {
        // Fallback for very old browsers: just mark everything visible
        document.querySelectorAll('.shopify-section').forEach(el => el.classList.add('is-in-viewport'));
      }

      // Defer massive layout thrashing tasks
      window.addEventListener('load', () => {
        requestIdle(() => this.optimizeImages());
      });
    }

    setupObserver() {
      this.observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in-viewport');

            // Allow CSS to trigger animations only when legitimate
            entry.target.setAttribute('data-visible', 'true');

            // Handle lazy background images or explicit lazy content
            this.handleLazyContent(entry.target);

            // Once visible, we generally don't need to observe strictly anymore for basic visibility
            // But we keep it if we want to toggle animations off when scrolling away (optional)
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: CONFIG.rootMargin,
        threshold: CONFIG.threshold
      });

      // Observe all sections
      document.querySelectorAll('.shopify-section, .lazy-load-wrapper').forEach(section => {
        this.observer.observe(section);
      });
    }

    handleLazyContent(target) {
      // 1. Lazy Backgrounds: data-bg-src
      const bgLazy = target.querySelectorAll('[data-bg-src]');
      bgLazy.forEach(el => {
        el.style.backgroundImage = `url('${el.dataset.bgSrc}')`;
        el.removeAttribute('data-bg-src');
      });

      // 2. Lazy Iframes/Videos: data-src
      const mediaLazy = target.querySelectorAll('iframe[data-src], video[data-src]');
      mediaLazy.forEach(el => {
        el.src = el.dataset.src;
        el.removeAttribute('data-src');
        if (el.tagName === 'VIDEO') el.load();
      });
    }

    optimizeImages() {
      // Helper to ensure native lazy loading is respected and handled correctly
      const images = document.querySelectorAll('img:not([loading])');
      images.forEach(img => {
        // If above fold, eager. If below, lazy.
        // Simplified: just add lazy to everything not explicitly marked eager
        // We avoid changing explicit 'eager' images
        if (img.getBoundingClientRect().top > window.innerHeight) {
          img.setAttribute('loading', 'lazy');
        }
      });
    }
  }

  // Initialize
  window.PerformanceOptimizer = new PerformanceOptimizer();

  // Export helpers for other scripts
  window.debounce = debounce;
  window.throttle = throttle;
  window.requestIdle = requestIdle;

})();
